import { useUser } from '@folklore/auth';
import { useRoutes, useUrlGenerator, useRoutesMatch } from '@folklore/routes';
import classNames from 'classnames';
import { lazy, Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useLocation } from 'wouter';

import { useAuthLogout } from '../../hooks/useAuth';
import { usePageViewTracking } from '../../hooks/useTracking';

import { useHasPermission, useHasJoinedGroup, useIsCheckingAuth } from '../../contexts/AuthContext';
import { useDefaultBrand } from '../../contexts/SiteContext';
import BrandLogoButton from '../buttons/BrandLogoButton';
import RoundedButton from '../buttons/RoundedButton';
import AccountMenu from '../menus/AccountMenu';
import AccountPanel from '../panels/AccountPanel';
import AccountProfilePanel from '../panels/AccountProfilePanel';
import AccountPromotionsPanel from '../panels/AccountPromotionsPanel';
import AccountSubscriptionsPanel from '../panels/AccountSubscriptionsPanel';
import AccountTimelinePanel from '../panels/AccountTimelinePanel';

import styles from '../../styles/pages/account-page.module.css';

const ProStatsPanel = lazy(() => import('../panels/ProStatsPanel'));

interface AccountPageProps {
    current?: boolean;
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: () => void;
    onLeave?: () => void;
}

function AccountPage({
    entered = false,
    entering = false,
    leaving = false,
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: AccountPageProps): JSX.Element | null {
    const defaultBrand = useDefaultBrand();

    // useScrollEnter({
    //     disabled: !current,
    //     onEnter: onEnter || undefined,
    //     onLeave: onLeave || undefined,
    // });

    const routes = useRoutes();
    const [location] = useLocation();
    const url = useUrlGenerator();
    const user = useUser();
    const { confirmed = false } = user || {};
    const hasAttributionAccess = useHasPermission('attribution');

    const isPro = useHasJoinedGroup('pro');
    // const confirmed = true;

    const isCheckingAuth = useIsCheckingAuth();
    const matchSubscriptions = useRoutesMatch(['account.subscriptions']);

    usePageViewTracking(current && user !== null && !isCheckingAuth, {
        pageType: 'account',
    });

    const { logout } = useAuthLogout();
    const onClickLogout = () => {
        logout();
    };

    if (user === null && (isCheckingAuth || !current)) {
        return null;
    }

    if ((user === null || (!confirmed && !matchSubscriptions)) && current) {
        return <Redirect to={`${url('login')}?next=${encodeURIComponent(location)}`} replace />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.entering]: entering,
                    [styles.leaving]: leaving,
                },
                className,
                contentClassName,
            ])}
            data-brand="urbania"
            data-theme="account"
        >
            <BrandLogoButton theme="light" brand={defaultBrand} className={styles.urbania} />
            <AccountMenu className={classNames([styles.menu, styles.maxWidth])} />
            <div className={styles.inner}>
                <Switch>
                    <Route path={routes['account.timeline']}>
                        <AccountTimelinePanel
                            className={classNames([styles.panel, styles.maxWidth])}
                        />
                    </Route>
                    <Route path={routes['account.subscriptions']}>
                        <AccountSubscriptionsPanel
                            className={classNames([styles.panel, styles.maxWidth])}
                        />
                    </Route>
                    <Route path={routes['account.profile']}>
                        <AccountProfilePanel
                            className={classNames([styles.panel, styles.maxWidth])}
                        />
                    </Route>
                    <Route path={routes['account.promotions']}>
                        <AccountPromotionsPanel
                            className={classNames([styles.panel, styles.maxWidth])}
                        />
                    </Route>
                    {isPro || hasAttributionAccess ? (
                        <Route path={routes['account.stats']}>
                            <Suspense
                                fallback={
                                    <div className={classNames([styles.panel, styles.maxWidth])} />
                                }
                            >
                                <ProStatsPanel
                                    className={classNames([styles.panel, styles.maxWidth])}
                                />
                            </Suspense>
                        </Route>
                    ) : null}
                    <Route>
                        <Suspense fallback={<div className={styles.panel} />}>
                            <AccountPanel
                                className={styles.panel}
                                innerClassName={styles.maxWidth}
                            />
                        </Suspense>
                    </Route>
                </Switch>
                <div className={styles.footer}>
                    <RoundedButton className={styles.logout} onClick={onClickLogout}>
                        <FormattedMessage
                            defaultMessage="Se déconnecter"
                            description="Button Label"
                        />
                    </RoundedButton>
                </div>
            </div>
        </div>
    );
}

export default AccountPage;
